import { Skeleton } from 'antd'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import { useCopyToolbar } from 'components/hyper-copy/UseCopyToolbar'
import { getAdjustedDateRangeString } from 'features/charts/utils'
import { derivePriorDateRange } from 'features/filters/prior-date-range'
import { FilterOption } from 'features/filters/types'
import { findDateWindow } from 'features/project/utils'
import {
  StatCard,
  StatValue,
} from 'features/summary/components/StatCard/StatCard'
import type { StatsComparisonResponse } from 'features/summary/service/types'
import type { SummaryRequest } from 'types/project'
import { DateWindows } from 'types/project'

const infoPanelObj: Record<
  string,
  { title: string; uvocTitle?: string; isUvoc: boolean }
> = {
  n_reviews_and_rating: {
    title: '# Records',
    uvocTitle: 'Records',
    isUvoc: true,
  },
  n_fragments: {
    title: 'Sentences',
    isUvoc: true,
  },
  n_rating_avg: {
    title: 'Rating',
    isUvoc: true,
  },
  n_sentiment_avg: {
    title: 'Sentiment',
    isUvoc: true,
  },
}

type Props = {
  statsComparison: StatsComparisonResponse | undefined
  comparisonLoading: boolean
  cardName?: string
  summaryRequest: SummaryRequest | null
  /** The period the stats describe; the prior period is derived from it. */
  dateRange: { startDate: number; endDate: number } | null
  filterList: FilterOption[]
  omittedOptions?: string[]
  columns?: number
  loading?: boolean
  projectId: string
  dateWindows: DateWindows
  defaultFilterList: FilterOption[]
}

export const Stats: React.FC<Props> = ({
  statsComparison,
  comparisonLoading,
  cardName,
  summaryRequest: _summaryRequest,
  dateRange,
  filterList: _filterList,
  omittedOptions,
  columns: _columns,
  loading = false,
  projectId,
  dateWindows,
  defaultFilterList,
}) => {
  const dateFilter = useMemo(
    () =>
      dateRange
        ? derivePriorDateRange(
            { min: dateRange.startDate, max: dateRange.endDate },
            dateWindows,
            defaultFilterList,
          ).range
        : undefined,
    [dateRange, dateWindows, defaultFilterList],
  )

  const {
    ref: rowRef,
    isHyperCopy: rowIsHyperCopy,
    selectForCopy: rowSelectForCopy,
  } = useCopyToolbar({ elementName: 'summary-info-panel' })

  const prevPeriodText = useMemo(() => {
    const label = findDateWindow(
      dateWindows,
      dateFilter?.min,
      dateFilter?.max,
    )?.label

    return label
      ? `in ${label}`
      : `from ${getAdjustedDateRangeString(dateFilter)}`
  }, [dateFilter, dateWindows])

  const hasPrevPeriod = (statsComparison?.previous?.length ?? 0) > 0

  const skeletonCard = (_index: number): JSX.Element => {
    return (
      <InfoCard>
        <SkeletonCardInner>
          <SkeletonTopRow>
            <Skeleton.Input
              active
              size="small"
              style={{ width: 90, height: 14 }}
            />
          </SkeletonTopRow>
          <Skeleton.Input
            active
            size="small"
            style={{ width: 60, height: 28 }}
          />
        </SkeletonCardInner>
      </InfoCard>
    )
  }

  const cardContent = (
    el: StatValue,
    prev?: StatValue,
    title?: string,
  ): JSX.Element | undefined => {
    const info = infoPanelObj[el.field]
    if (!info) return

    return (
      <StatCard
        key={el.field}
        el={el}
        prev={prev}
        label={info.uvocTitle ?? info.title}
        title={title}
        projectId={projectId}
        hasPrevPeriod={hasPrevPeriod}
        prevPeriodText={prevPeriodText}
        comparisonLoading={comparisonLoading}
        dateFilter={dateFilter}
        onCopyRow={rowSelectForCopy}
      />
    )
  }

  return (
    <>
      {cardName ? (
        <>
          {loading
            ? skeletonCard(0)
            : (() => {
                const current = statsComparison?.current?.find(
                  (el) => el.field === cardName,
                )
                if (!current) return null
                const prev = statsComparison?.previous?.find(
                  (el) => el.field === cardName,
                )
                return cardContent(current, prev, infoPanelObj[cardName]?.title)
              })()}
        </>
      ) : (
        <InfoCardWrapper
          ref={rowRef}
          className={rowIsHyperCopy ? 'hyper-copy-active' : 'hyper-copy'}
          onClick={(e) => {
            if (rowIsHyperCopy) {
              rowSelectForCopy()
              e.stopPropagation()
            }
          }}
        >
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <React.Fragment key={`skeleton-${index}`}>
                  {skeletonCard(index)}
                </React.Fragment>
              ))
            : statsComparison?.current?.map((el, index) =>
                !(omittedOptions ?? []).includes(el.field) &&
                infoPanelObj[el.field]?.isUvoc
                  ? cardContent(
                      el,
                      statsComparison?.previous?.[index],
                      infoPanelObj[el.field]?.title,
                    )
                  : null,
              )}
        </InfoCardWrapper>
      )}
    </>
  )
}

const InfoCardWrapper = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`

// Frame for the loading skeleton only — the real card frame (with the
// hover-revealed kebab menu) lives in StatCard.tsx.
const InfoCard = styled.div`
  display: flex;
  flex: 1 1 0%;
  min-width: 160px;
  background: #fff;
  border: 1px solid rgb(216, 216, 216);
  border-radius: 8px;
  box-shadow:
    rgba(0, 0, 0, 0.02) 0px 3px 6px -2px,
    rgba(0, 0, 0, 0.04) 0px 1px 1px;
  overflow: hidden;
`

const SkeletonCardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  width: 100%;
`

const SkeletonTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`
